import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Divider,
  Checkbox,
  Box,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  Link,
  SimpleGrid,
  Badge,
} from "@chakra-ui/react";
import { 
  MdStars, 
  MdWarning, 
  MdInfo, 
  MdCheckCircle,
  MdBarChart,
  MdInventory,
  MdTableBar,
  MdRestaurantMenu,
  MdSentimentSatisfied,
  MdCampaign,
  MdSupport,
  MdTrendingUp,
  MdAnalytics,
  MdAssignment,
  MdPeople,
  MdSpeed
} from "react-icons/md";
import { pricingService } from "../../services/pricingService";

export default function PremiumUpgradeModal({ isOpen, onClose, onConfirm }) {
  const [pricingData, setPricingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedKVKK, setAcceptedKVKK] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const toast = useToast();

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgColor = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");

  useEffect(() => {
    if (isOpen) {
      fetchPricingData();
      // Modal açıldığında checkbox'ları sıfırla
      setAcceptedTerms(false);
      setAcceptedKVKK(false);
    }
  }, [isOpen]);

  const fetchPricingData = async () => {
    try {
      setIsLoading(true);
      const response = await pricingService.getPricingInfo();
      if (response.success) {
        setPricingData(response.data);
      }
    } catch (error) {
      console.error("Fiyatlandırma bilgisi getirilirken hata:", error);
      toast({
        title: "Hata",
        description: "Fiyatlandırma bilgileri yüklenirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calculatePremiumPricing = () => {
    if (!pricingData) return null;

    const currentPricing = pricingData.pricing;
    const category = pricingData.restaurant.pricingCategory;
    
    // PREMIUM için sabit ücret
    const PREMIUM_MONTHLY_FEE = 899; // Sabit premium ücret
    const newCommissionRate = currentPricing.commissionRate; // Komisyon oranı aynı kalır
    const newCommissionAmount = (pricingData.currentMonthRevenue * newCommissionRate) / 100;
    const newTotalFee = PREMIUM_MONTHLY_FEE + newCommissionAmount;

    return {
      current: {
        monthlyFee: currentPricing.monthlyFee,
        commissionRate: currentPricing.commissionRate,
        commissionAmount: currentPricing.currentMonthCommission,
        totalFee: currentPricing.currentMonthTotal,
      },
      premium: {
        monthlyFee: PREMIUM_MONTHLY_FEE,
        commissionRate: newCommissionRate,
        commissionAmount: newCommissionAmount,
        totalFee: newTotalFee,
      },
      category: category,
    };
  };

  const handleUpgrade = async () => {
    if (!acceptedTerms || !acceptedKVKK) {
      toast({
        title: "Eksik Bilgi",
        description: "Lütfen tüm sözleşmeleri kabul edin",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setIsUpgrading(true);
      // Backend'e upgrade isteği gönder
      const response = await pricingService.upgradeToPremium();
      
      if (response.success) {
        toast({
          title: "Başarılı",
          description: "PREMIUM plana başarıyla yükseltildi!",
          status: "success",
          duration: 3000,
        });
        
        // Callback'i çağır (plan bilgisini güncellemek için)
        if (onConfirm) {
          await onConfirm();
        }
        
        onClose();
      }
    } catch (error) {
      console.error("Yükseltme hatası:", error);
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Yükseltme işlemi sırasında bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const premiumPricing = calculatePremiumPricing();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>
          <HStack>
            <Icon as={MdStars} color="yellow.400" boxSize={6} />
            <Text>PREMIUM'a Yükselt</Text>
          </HStack>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Uyarı Mesajı */}
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold" mb={1}>
                  Önemli Bilgilendirme
                </Text>
                <Text fontSize="sm">
                  PREMIUM plana geçiş yaptığınızda sözleşmeniz otomatik olarak güncellenecek ve 
                  aylık sabit ücret ile komisyon ücretiniz değişecektir.
                </Text>
              </Box>
            </Alert>

            {/* Mevcut Fiyatlandırma */}
            {premiumPricing && (
              <>
                <Box>
                  <Text fontWeight="bold" mb={3} color={textColor}>
                    Fiyatlandırma Karşılaştırması
                  </Text>
                  <SimpleGrid columns={2} spacing={4}>
                    <Box p={4} border="1px" borderColor={borderColor} borderRadius="md">
                      <Text fontSize="sm" color="gray.500" mb={2}>STANDARD Plan</Text>
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        ₺{premiumPricing.current.monthlyFee.toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Aylık Sabit Ücret
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        + %{premiumPricing.current.commissionRate} Komisyon
                      </Text>
                    </Box>
                    <Box p={4} border="2px" borderColor="yellow.400" borderRadius="md" bg="yellow.50" _dark={{ bg: "yellow.900" }}>
                      <HStack mb={2}>
                        <Text fontSize="sm" fontWeight="bold" color="yellow.600">
                          PREMIUM Plan
                        </Text>
                        <Badge colorScheme="yellow" fontSize="xs">₺899/ay</Badge>
                      </HStack>
                      <Text fontSize="lg" fontWeight="bold" color={textColor}>
                        ₺{premiumPricing.premium.monthlyFee.toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Aylık Sabit Ücret
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        + %{premiumPricing.premium.commissionRate} Komisyon
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                {/*<Box p={4} bg="gray.50" _dark={{ bg: "navy.700" }} borderRadius="md">
                  <Text fontSize="sm" fontWeight="bold" mb={2} color={textColor}>
                    Bu Ayın Tahmini Ücreti
                  </Text>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">
                      STANDARD: ₺{premiumPricing.current.totalFee.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                    <Icon as={MdInfo} color="gray.400" />
                    <Text fontSize="sm" fontWeight="bold" color="yellow.600">
                      PREMIUM: ₺{premiumPricing.premium.totalFee.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                     Komisyon ücreti aylık ciroyuza göre değişiklik gösterebilir
                  </Text>
                </Box>*/}
              </>
            )}

            <Divider />

            {/* Sözleşme Onayları */}
            <VStack align="stretch" spacing={3}>
              <Text fontWeight="bold" color={textColor}>
                Sözleşme Onayları
              </Text>

              <Checkbox
                isChecked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                colorScheme="brand"
              >
                <Text fontSize="sm">
                  <Link href="/terms?type=business" target="_blank" color="blue.500" textDecoration="underline">
                    Kullanım Sözleşmesi
                  </Link>
                  'ni okudum ve kabul ediyorum. PREMIUM plana geçiş yaptığımda sözleşme otomatik olarak güncellenecektir.
                </Text>
              </Checkbox>

              <Checkbox
                isChecked={acceptedKVKK}
                onChange={(e) => setAcceptedKVKK(e.target.checked)}
                colorScheme="brand"
              >
                <Text fontSize="sm">
                  <Link href="/kvkk?type=business" target="_blank" color="blue.500" textDecoration="underline">
                    Aydınlatma Metni ve KVKK Sözleşmesi
                  </Link>
                  'ni okudum ve kabul ediyorum.
                </Text>
              </Checkbox>
            </VStack>

            {/* Premium Özellikler */}
            <Box 
              p={4} 
              borderRadius="lg" 
              bg={bgColor}
              border="2px solid"
              borderColor="yellow.400"
            >
              <HStack mb={3}>
                <Icon as={MdStars} color="yellow.500" boxSize={5} />
                <Text fontWeight="bold" fontSize="lg" color={textColor}>
                  PREMIUM Plan Özellikleri
                </Text>
              </HStack>
              <SimpleGrid columns={1} spacing={3}>
                <HStack align="start" spacing={3}>
                  <Icon as={MdBarChart} color="green.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      📊 Gelişmiş Gelir & Sipariş Analizi
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Aylık/yıllık gelir trendleri, sipariş analizleri ve detaylı raporlar
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdInventory} color="blue.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      📦 Akıllı Stok Yönetimi
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Otomatik stok takibi, tazelik uyarıları ve kritik seviye bildirimleri
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdTableBar} color="purple.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      🪑 Masa & Görev Analizi
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Masa doluluk oranları, görev tamamlama istatistikleri ve verimlilik raporları
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdRestaurantMenu} color="orange.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      🍽️ Menü Performans Analizi
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      En çok satan ürünler, kategori bazlı analizler ve karlılık raporları
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdSentimentSatisfied} color="pink.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      ⭐ Müşteri Değerlendirmeleri
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Gerçek zamanlı müşteri geri bildirimleri ve değerlendirme analizleri
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdCampaign} color="red.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      🎯 Kendi Kampanyalarınızı Oluşturun
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Kendi kampanyalarınızı oluşturun ve müşterilerinize sunun
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdPeople} color="teal.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      👥 Çalışan Yönetimi
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Sınırsız çalışan ekleme ve görev atama özellikleri
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdSupport} color="cyan.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      🚀 Öncelikli 7/24 Destek
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Hızlı yanıt süresi ve öncelikli teknik destek hizmeti
                    </Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={MdRestaurantMenu} color="green.500" boxSize={5} mt={0.5} />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color={textColor}>
                      🍔 Sınırsız Ürün Ekleme
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      STANDARD plan 40 ürün ile sınırlıdır, PREMIUM'da sınırsız ürün ekleyebilirsiniz
                    </Text>
                  </Box>
                </HStack>
              </SimpleGrid>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            İptal
          </Button>
          <Button
            colorScheme="yellow"
            leftIcon={<Icon as={MdStars} />}
            onClick={handleUpgrade}
            isLoading={isUpgrading}
            isDisabled={!acceptedTerms || !acceptedKVKK}
          >
            PREMIUM'a Yükselt
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

